/**
 * useMazeGenerator - Generates maze from predefined templates
 * Simple approach: templates instead of procedural generation (KISS)
 */

import { useCallback } from 'react';
import type { Maze, MazeCell, GridPosition, GhostSpawnPoint, MovementAxis } from '../types/pacman';
import { MAZE_WIDTH, MAZE_HEIGHT } from '../types/pacman';

// Predefined maze templates
// # = wall, . = path with dot
const MAZE_TEMPLATES = [
  `
#####################
#.........#.........#
#.###.###.#.###.###.#
#...................#
###.#####.#.#####.###
#.........#.........#
#.###.#########.###.#
#.........#.........#
###.#####.#.#####.###
#...................#
#.###.###.#.###.###.#
#.........#.........#
#####################
`.trim(),
  `
#####################
#...................#
#.###.#.#####.#.###.#
#.#...#...#...#...#.#
#.#.#####.#.#####.#.#
#...................#
###.###.###.###.###.#
#...................#
#.#.#####.#.#####.#.#
#.#...#...#...#...#.#
#.###.#.#####.#.###.#
#...................#
#####################
`.trim(),
];

export function useMazeGenerator() {
  const generateMaze = useCallback((ghostCount: number): Maze => {
    const templateIndex = Math.floor(Math.random() * MAZE_TEMPLATES.length);
    const template = MAZE_TEMPLATES[templateIndex];
    const cells = parseTemplate(template);
    const pacmanSpawn = findPacmanSpawn(cells);
    const ghostSpawns = findGhostSpawns(cells, ghostCount, pacmanSpawn);

    return {
      width: MAZE_WIDTH,
      height: MAZE_HEIGHT,
      cells,
      pacmanSpawn,
      ghostSpawns,
    };
  }, []);

  return { generateMaze };
}

function parseTemplate(template: string): MazeCell[][] {
  const lines = template.split('\n');
  return lines.map((line, y) =>
    line.split('').map((char, x) => ({
      type: char === '#' ? 'WALL' : 'DOT',
      position: { x, y },
    }))
  );
}

function findPacmanSpawn(cells: MazeCell[][]): GridPosition {
  const centerY = Math.floor(cells.length / 2);
  const centerX = Math.floor(cells[0].length / 2);

  // Find nearest free cell from center
  for (let r = 0; r < 5; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const y = centerY + dy;
        const x = centerX + dx;
        if (cells[y]?.[x]?.type !== 'WALL') {
          return { x, y };
        }
      }
    }
  }
  return { x: centerX, y: centerY };
}

function findGhostSpawns(
  cells: MazeCell[][],
  count: number,
  pacmanSpawn: GridPosition
): GhostSpawnPoint[] {
  const corridors = findCorridors(cells);
  const minDistance = 4;

  // Filter corridors far enough from Pac-Man
  const validCorridors = corridors.filter(c => {
    const dist = Math.abs(c.position.x - pacmanSpawn.x) +
                 Math.abs(c.position.y - pacmanSpawn.y);
    return dist >= minDistance;
  });

  // Shuffle and pick
  const shuffled = validCorridors.sort(() => Math.random() - 0.5);
  const spawns: GhostSpawnPoint[] = [];

  for (let i = 0; i < count && i < shuffled.length; i++) {
    spawns.push(shuffled[i]);
  }

  // Fallback if not enough corridors
  while (spawns.length < count) {
    const fallbackX = 3 + (spawns.length % 4) * 4;
    const fallbackY = spawns.length < 4 ? 3 : 9;
    spawns.push({
      position: { x: fallbackX, y: fallbackY },
      axis: 'HORIZONTAL',
    });
  }

  return spawns;
}

function findCorridors(cells: MazeCell[][]): GhostSpawnPoint[] {
  const corridors: GhostSpawnPoint[] = [];

  for (let y = 1; y < cells.length - 1; y++) {
    for (let x = 1; x < cells[0].length - 1; x++) {
      if (cells[y][x].type === 'WALL') continue;

      const left = cells[y][x - 1]?.type !== 'WALL';
      const right = cells[y][x + 1]?.type !== 'WALL';
      const up = cells[y - 1]?.[x]?.type !== 'WALL';
      const down = cells[y + 1]?.[x]?.type !== 'WALL';

      const isHorizontal = left && right;
      const isVertical = up && down;

      // Pure corridor (not intersection)
      let axis: MovementAxis | null = null;
      if (isHorizontal && !isVertical) {
        axis = 'HORIZONTAL';
      } else if (isVertical && !isHorizontal) {
        axis = 'VERTICAL';
      }

      if (axis) {
        corridors.push({ position: { x, y }, axis });
      }
    }
  }

  return corridors;
}
